import { get, isNil, sortBy } from 'lodash'
import {
  WEBSOCKET_URL,
  ACTION_CABLE_COMMANDS,
  ACTION_CABLE_TYPES,
  SHELL_MESSAGE_TYPES,
} from '../constants'

const SEQUENCE_TIMEOUT_MS = 1000

const createShellClient = (options = {}) => {
  const {
    getToken,
    sessionId,
    onStarted,
    onOutput,
    onExit,
    onError,
    onConnect,
    onDisconnect,
  } = options

  let socket = null
  let isConnecting = false
  let identifier = null
  let subscriptionConfirmed = false
  let earlyMessages = []

  // Sequenced output handling
  let nextExpectedSequence = 1
  let outputBuffer = {}
  let sequenceTimeoutId = null

  const processOutput = (message) => {
    const outputData = get(message, 'data')
    if (onOutput && outputData) {
      onOutput(outputData)
    }
  }

  const flushOutputBuffer = () => {
    // Process all consecutive messages starting from nextExpectedSequence
    while (outputBuffer[nextExpectedSequence]) {
      const message = outputBuffer[nextExpectedSequence]
      delete outputBuffer[nextExpectedSequence]
      processOutput(message)
      nextExpectedSequence++
    }
  }

  const flushAllBuffered = () => {
    // Timeout fired - flush all buffered messages in order, even if gaps exist
    clearTimeout(sequenceTimeoutId)
    sequenceTimeoutId = null

    const sequences = sortBy(Object.keys(outputBuffer).map(Number))
    sequences.forEach((seq) => {
      processOutput(outputBuffer[seq])
      delete outputBuffer[seq]
    })

    // Set next expected to highest + 1
    if (sequences.length > 0) {
      nextExpectedSequence = sequences[sequences.length - 1] + 1
    }
  }

  const scheduleTimeout = () => {
    if (sequenceTimeoutId) return // Already scheduled
    sequenceTimeoutId = setTimeout(flushAllBuffered, SEQUENCE_TIMEOUT_MS)
  }

  const handleSequencedOutput = (message) => {
    const sequence = get(message, 'seq')

    // If no sequence, process immediately (backwards compatibility)
    if (isNil(sequence)) {
      processOutput(message)
      return
    }

    if (sequence === nextExpectedSequence) {
      // Expected sequence - process and flush any buffered
      processOutput(message)
      nextExpectedSequence++
      flushOutputBuffer()

      // Clear timeout if buffer is empty
      if (Object.keys(outputBuffer).length === 0 && sequenceTimeoutId) {
        clearTimeout(sequenceTimeoutId)
        sequenceTimeoutId = null
      }
    } else if (sequence > nextExpectedSequence) {
      // Out of order - buffer and schedule timeout
      outputBuffer[sequence] = message
      scheduleTimeout()
    } else {
      // Ignore sequences < nextExpectedSequence (duplicates)
    }
  }

  const buildIdentifier = () => {
    return JSON.stringify({ channel: 'ShellChannel', session_id: sessionId })
  }

  const sendCommand = (command, data = {}) => {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      console.warn('ShellClient: Socket not ready')
      return false
    }

    const message = {
      command,
      identifier,
      ...data,
    }

    socket.send(JSON.stringify(message))
    return true
  }

  const handleMessage = (data) => {
    let message
    try {
      message = JSON.parse(data)
    } catch (error) {
      console.error('ShellClient: Failed to parse message', error)
      return
    }

    const type = get(message, 'type')

    switch (type) {
      case ACTION_CABLE_TYPES.WELCOME:
        // Connection established, subscribe to shell channel
        identifier = buildIdentifier()
        sendCommand(ACTION_CABLE_COMMANDS.SUBSCRIBE)
        break

      case ACTION_CABLE_TYPES.PING:
        // Server keep-alive, no action needed
        break

      case ACTION_CABLE_TYPES.CONFIRM_SUBSCRIPTION:
        // Subscription confirmed - shell session is ready
        subscriptionConfirmed = true
        // Flush early messages that arrived before subscription was confirmed
        earlyMessages.forEach((msg) => handleShellMessage(msg))
        earlyMessages = []
        if (onStarted) onStarted()
        break

      case ACTION_CABLE_TYPES.REJECT_SUBSCRIPTION:
        if (onError) {
          onError('Subscription rejected')
        }
        break

      default:
        // Regular message with data
        if (message.message) {
          if (!subscriptionConfirmed) {
            // Buffer messages that arrive before subscription is confirmed
            earlyMessages.push(message.message)
          } else {
            handleShellMessage(message.message)
          }
        }
        break
    }
  }

  const handleShellMessage = (message) => {
    const messageType = get(message, 'type')

    switch (messageType) {
      case SHELL_MESSAGE_TYPES.STARTED:
        if (onStarted) onStarted()
        break

      case SHELL_MESSAGE_TYPES.OUTPUT:
        handleSequencedOutput(message)
        break

      case SHELL_MESSAGE_TYPES.EXIT:
        const exitCode = get(message, 'exit_code')
        const reason = get(message, 'reason')
        if (onExit) onExit(exitCode, reason)
        break

      case SHELL_MESSAGE_TYPES.ERROR:
        const errorMsg = get(message, 'error')
        if (onError) onError(errorMsg)
        break

      default:
        // Handle messages with empty/missing type but data present (treat as output)
        if (get(message, 'data')) {
          handleSequencedOutput(message)
        }
        break
    }
  }

  const connect = () => {
    if (isConnecting) {
      return Promise.reject(new Error('Connection already in progress'))
    }

    if (socket && socket.readyState === WebSocket.OPEN) {
      return Promise.resolve()
    }

    if (socket) {
      socket.onclose = null
      socket.onerror = null
      socket.onmessage = null
      socket.close(1000, 'Reconnecting')
      socket = null
    }

    isConnecting = true

    return getToken()
      .then((token) => {
        if (isNil(token)) {
          isConnecting = false
          return Promise.reject(new Error('No token available'))
        }

        const url = `${WEBSOCKET_URL}?token=${token}`

        return new Promise((resolve, reject) => {
          socket = new WebSocket(url)

          socket.onopen = () => {
            isConnecting = false
            if (onConnect) onConnect()
            resolve()
          }

          socket.onclose = (event) => {
            isConnecting = false
            if (onDisconnect) onDisconnect(event)
          }

          socket.onerror = (error) => {
            isConnecting = false
            if (onError) onError(error)
            reject(error)
          }

          socket.onmessage = (event) => {
            handleMessage(event.data)
          }
        })
      })
      .catch((error) => {
        isConnecting = false
        throw error
      })
  }

  const sendInput = (data) => {
    if (!identifier) return false

    const encodedData = btoa(data)
    const messageData = JSON.stringify({ action: 'input', data: encodedData })
    return sendCommand(ACTION_CABLE_COMMANDS.MESSAGE, { data: messageData })
  }

  const sendResize = (cols, rows) => {
    if (!identifier) return false

    const messageData = JSON.stringify({ action: 'resize', cols, rows })
    return sendCommand(ACTION_CABLE_COMMANDS.MESSAGE, { data: messageData })
  }

  const disconnect = () => {
    isConnecting = false

    if (socket) {
      if (identifier) {
        sendCommand(ACTION_CABLE_COMMANDS.UNSUBSCRIBE)
      }
      socket.onclose = null
      socket.onerror = null
      socket.onmessage = null
      socket.close(1000, 'Client disconnect')
      socket = null
    }

    identifier = null
    subscriptionConfirmed = false
    earlyMessages = []

    // Clear sequence state
    if (sequenceTimeoutId) {
      clearTimeout(sequenceTimeoutId)
      sequenceTimeoutId = null
    }
    nextExpectedSequence = 1
    outputBuffer = {}
  }

  const isConnected = () => {
    return socket && socket.readyState === WebSocket.OPEN
  }

  return {
    connect,
    disconnect,
    sendInput,
    sendResize,
    isConnected,
  }
}

export default createShellClient
